<script lang="ts">
  import { createForm } from "felte";
  import Fooji from "@fooji/microsites";
  import GooglePlacesInput from "./GooglePlacesInput.svelte";
  import type { Place, ValidatedAddress } from "../lib/helpers/googlePlaces";
  import { handlePlace } from "../lib/helpers/googlePlaces";
  import type { PlaceResult } from "../lib/google/maps/types";
  import { addressStore } from "../stores/address";
  export let onFormComplete = () => {};
  import { turnstile } from "@svelte-put/cloudflare-turnstile";
  import type {
    TurnstileEventAttributes,
    TurnstileEventDetail,
  } from "@svelte-put/cloudflare-turnstile";
  import { assessTurnstile } from "../lib/api/auth";

  const turnstileSiteKey = import.meta.env.FOOJI_TURNSTILE_SITE_KEY as string;

  let selectedGoogleAddress;
  let foojiAddress;
  let isButtonDisabledByCaptcha = true;
  let isLoading = false;
  let invalidAddress = false;

  let hiltonHonorsNumberInput;

  const { form } = createForm({
    onSubmit(values, context) {
      return values;
    },
    async onSuccess(response, context) {
      isLoading = true;

      const data = {
        hiltonHonorsNumber: response.hiltonHonorsNumber,
        first_name: response.firstLastName,
        last_name: response.firstLastName,
        business: response.businessName,
        address_id: foojiAddress.id,
        country: response.country,
        phone: response.phone,
        email: response.email,
      };
      const [userData] = await Promise.all([
        Fooji.User.get(),
        Fooji.User.update(data),
        Fooji.UserSettings.save({
          country: response.country,
          hiltonHonorsNumber: response.hiltonHonorsNumber,
        }),
      ]);
      onFormComplete();
      isLoading = false;
    },
    onError(err, context) {
      console.log("onError", err);
    },
  });

  const onPlaceSelected = async (selectedPlace: PlaceResult) => {
    const { isValid, address } = handlePlace(selectedPlace);

    // I guess we need to check for the address because handlePlace can return isValid without an address
    if (isValid && address) {
      console.log("Selected address", address);
      selectedGoogleAddress = address;
      foojiAddress = await Fooji.Address.createOrUpdate({
        ...selectedGoogleAddress,
        address2: "",
        note: "",
      });
      addressStore.set(foojiAddress);
    }

    invalidAddress = !(isValid && address);
  };

  const onTurnstileComplete: TurnstileEventAttributes["on:turnstile"] = (e) => {
    void (async () => {
      await doOnTurnstileComplete(e.detail);
    })();
  };

  const doOnTurnstileComplete = async (
    detail: TurnstileEventDetail<{ token: string }>
  ) => {
    let result;
    try {
      result = await assessTurnstile(detail.token);
      isButtonDisabledByCaptcha = !result.success;
    } catch (e) {
      console.error("Error assessing turnstile", e);
      // let user try again
      detail.turnstile.reset(detail.widgetId);
      isButtonDisabledByCaptcha = true;
    }
  };

  // By default, Turnstile will try again
  const onTurnstileExpired = () => {
    isButtonDisabledByCaptcha = true;
  };

  const onTurnstileError: TurnstileEventAttributes["on:turnstile:error"] = (
    e
  ) => {
    console.error("Turnstile error", e.detail);
    const turnstileErrorCode = e.detail?.code;
    // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
    // 300*** (300030 or 300031 for example): Possible bot detected
    // 600*** (600010 for example): Possible bot detected
    // 106*** (106010 for example): Invalid Parameters. Continuous failures may indicate a bot
    // 11060* (110600 for example): User probably took too long to complete the captcha
    if (
      ![/^300/, /^11060/, /^106/, /^600/].some((regex) =>
        regex.test(turnstileErrorCode)
      )
    ) {
      // Bugsnag.getClient().notify(
      //   new Error(`Turnstile error ${JSON.stringify(e.detail)}`)
      // );
    }
  };

  // For now just log to Bugsnag, hopefully this will be very rare
  const onTurnstileUnsupported: TurnstileEventAttributes["on:turnstile:unsupported"] =
    (e) => {
      console.error("Browser unsupported by turnstile", e.detail);
      // Bugsnag.getClient().notify(
      //   new Error(
      //     `Browser unsupported by turnstile ${JSON.stringify(e.detail)}`
      //   )
      // );
    };

  const validateHiltonHonorsNumber = (evt) => {
    const number = evt.target.value;
    if (number.length < 9 || number.match(/\D/)) {
      hiltonHonorsNumberInput.setCustomValidity(
        "Please enter a valid Hilton Honors number"
      );
    } else {
      hiltonHonorsNumberInput.setCustomValidity("");
    }
  };
</script>

<section class="px-3.5 md:px-7 pb-7 md:pb-20" id="form">
  <div class="container-center">
    <p class="font-normal md:block hidden">All fields required</p>
    <div class="mt-7">
      <form use:form action="" class="space-y-7">
        <input
          bind:this={hiltonHonorsNumberInput}
          required
          inputmode="numeric"
          pattern="\d+"
          minlength="9"
          maxlength="10"
          on:input={validateHiltonHonorsNumber}
          title="Please enter a valid Hilton Honors Number"
          name="hiltonHonorsNumber"
          type="text"
          placeholder="Hilton Honors Number (associated with your Hilton for Business program)"
          class="bg-primary-off-white py-5 md:py-3.5 px-6 md:px-8 font-normal rounded-lg placeholder:text-black text-base outline-none w-full"
        />
        <a
          href="https://www.hilton.com/en/hilton-honors/login/forgot-information/"
          class="text-sm"
          target="_blank">Forgot your Hilton Honors Number? Click here.</a
        >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-7">
          <input
            required
            name="firstLastName"
            type="text"
            placeholder="First/Last Name (must match Hilton Honors program account information)"
            class="bg-primary-off-white py-5 md:py-3.5 px-6 md:px-8 font-normal rounded-lg placeholder:text-black text-base outline-none w-full"
          />
          <input
            required
            name="businessName"
            type="text"
            placeholder="Business Name"
            class="bg-primary-off-white py-5 md:py-3.5 px-6 md:px-8 font-normal rounded-lg placeholder:text-black text-base outline-none w-full"
          />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-7">
          <div class="flex flex-col">
            <GooglePlacesInput placeholder="Business Address" {onPlaceSelected}
            ></GooglePlacesInput>

            {#if invalidAddress}
              <p class="text-xs m-1">
                Please make sure to select a valid address.
              </p>
            {/if}
          </div>
          <div class="relative z-0">
            <select
              required
              name="country"
              class="bg-primary-off-white py-5 md:py-3.5 px-6 md:px-8 font-normal rounded-lg placeholder:text-black text-base outline-none w-full appearance-none"
            >
              <option value="us">United States of America</option>
              <option value="uk"
                >United Kingdom (excluding Norther Ireland)</option
              >
            </select>
            <span
              class="absolute top-1/2 -translate-y-1/2 right-7 pointer-events-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M1 6L10 14L19 6"
                  stroke="black"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </span>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-7">
          <input
            required
            name="phone"
            type="phone"
            placeholder="Business Phone Number"
            class="bg-primary-off-white py-5 md:py-3.5 px-6 md:px-8 font-normal rounded-lg placeholder:text-black text-base outline-none w-full"
          />
          <input
            required
            name="email"
            type="email"
            placeholder="Business Email"
            class="bg-primary-off-white py-5 md:py-3.5 px-6 md:px-8 font-normal rounded-lg placeholder:text-black text-base outline-none w-full"
          />
        </div>
        <p class="text-xs">
          By clicking ‘Enter Sweepstakes,’ I consent to being contacted by text
          message, email, or phone call regarding my participation and any
          delivery details related to the Sweepstakes. Communication may be sent
          to the contact information I have provided, and automated technology
          may be used for this purpose. I understand that agreeing to receive
          such communications is not a condition of entry into the Sweepstakes.
        </p>
        <div
          class="flex items-start sm:items-center gap-7 md:flex-row flex-col"
        >
          <div
            role="captcha"
            use:turnstile
            turnstile-sitekey={turnstileSiteKey}
            turnstile-theme="light"
            turnstile-action={"unknown"}
            on:turnstile={onTurnstileComplete}
            on:turnstile:expired={onTurnstileExpired}
            on:turnstile:error={onTurnstileError}
            on:turnstile:unsupported={onTurnstileUnsupported}
          />
          <button
            disabled={isButtonDisabledByCaptcha ||
              isLoading ||
              invalidAddress ||
              !foojiAddress}
            type="submit"
            class="text-lg font-bold flex items-center justify-center leading-6 text-white h-[48px] md:h-60 md:h-60 px-8 md:px-12 rounded-lg disabled:bg-my-light-gray bg-primary-blue transition-all duration-300 ease-in-out hover:opacity-80"
          >
            Enter sweepstakes
          </button>
        </div>
      </form>
    </div>
  </div>
</section>
